import { useAppSelector } from "app/hooks";
import { useAuth } from "hooks/auth";
import { InfiniteResponse } from "hooks/useManageInfiniteScroll";
import { querystring } from "models/Listing";
import { selectCurrentStoreData } from "reduxState/currentStoreData/currentStoreDataSlice";
import useSWR, { SWRResponse } from "swr";
import useSWRInfinite from "swr/infinite";
import {
  AchievementBEData,
  AchievementBEFormatWithoutRuleId,
  AchievementListItem,
  AddAchievementBatchResponse,
  MembersAchievementProgress,
  PatchAchievementBEFormat,
  TriggeredAchievementListItem,
} from "types/achievements";
import { ListingProps, ListResponse } from "types/listing";
import { getEnvVariable } from "utilities/env";

export const useAchievements = () => {
  const { code } = useAppSelector(selectCurrentStoreData);
  const { fetcher, arrayFetcher } = useAuth();

  const useAchievementList = (
    props?: ListingProps,
  ): SWRResponse<ListResponse<AchievementListItem>> => {
    return useSWR(
      () =>
        code
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${code}/achievement${querystring(props)}`
          : null,
      fetcher,
    );
  };

  const useInfiniteAchievmentsList = (props?: ListingProps) => {
    return useSWRInfinite<InfiniteResponse<AchievementListItem>>(
      (index) =>
        code
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${code}/achievement${querystring({ ...props, page: index + 1 })}`
          : null,
      fetcher,
      { revalidateFirstPage: false },
    );
  };

  const getAchievementBatchData = async (
    values: { name: string; achievementId: string }[],
  ) => {
    if (!code) return;

    const responses = await Promise.all(
      values.map(({ achievementId }) =>
        fetcher(
          `${getEnvVariable(
            "REACT_APP_API_URL",
          )}/${code}/achievement/${achievementId}`,
          {
            headers: { "Content-Type": "application/json" },
            method: "GET",
          },
        ),
      ),
    )
      .then((results) =>
        Promise.all(results.map((r) => (r.json ? r.json() : r))),
      )
      .then((resp) =>
        resp.map(({ achievementId, ...rest }, index) => ({
          ...rest,
          name: values[index].name,
        })),
      );

    return responses;
  };

  const addAchievementBatch = async (values: any[]) => {
    if (!code) return;

    const responses: AddAchievementBatchResponse = await Promise.all(
      values.map(({ achievementId, name, ...rest }) =>
        achievementId
          ? new Promise((resolve) =>
              resolve({
                achievementId,
                ...rest,
              }),
            )
          : fetcher(
              `${getEnvVariable("REACT_APP_API_URL")}/${code}/achievement`,
              {
                body: JSON.stringify({
                  achievement: { ...rest, active: false },
                }),
                headers: { "Content-Type": "application/json" },
                method: "POST",
              },
            ),
      ),
    )
      .then((results) =>
        Promise.all(results.map((r) => (r.json ? r.json() : r))),
      )
      .then((resp) =>
        resp.map((r, i) => ({
          ...r,
          data: values[i],
        })),
      );
    return responses;
  };

  const addAchievement = async (payload: AchievementBEFormatWithoutRuleId) => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/${code}/achievement`,
      {
        body: JSON.stringify({ achievement: payload }),
        headers: { "Content-Type": "application/json" },
        method: "post",
      },
    );
    const data = await response.json();
    if (response.ok) {
      return { data };
    } else {
      throw data;
    }
  };

  const editAchievement = async (
    achievementID: string,
    payload: AchievementBEFormatWithoutRuleId,
  ) => {
    const response = await fetcher(
      `${getEnvVariable(
        "REACT_APP_API_URL",
      )}/${code}/achievement/${achievementID}`,
      {
        body: JSON.stringify({ achievement: payload }),
        headers: { "Content-Type": "application/json" },
        method: "put",
      },
    );
    if (response.status === 204) {
      return true;
    } else {
      throw await response.json();
    }
  };

  const manageProgress = async (
    achievementID: string,
    memberID: string,
    payload: MembersAchievementProgress,
  ) => {
    const response = await fetcher(
      `${getEnvVariable(
        "REACT_APP_API_URL",
      )}/${code}/member/${memberID}/achievement/${achievementID}/progress`,
      {
        body: JSON.stringify({ memberProgress: payload }),
        headers: { "Content-Type": "application/json" },
        method: "PATCH",
      },
    );
    if (response.status === 204) {
      return true;
    } else {
      throw await response.json();
    }
  };

  const patchAchievement = async (
    achievementID: string,
    payload: Partial<PatchAchievementBEFormat>,
  ) => {
    const response = await fetcher(
      `${getEnvVariable(
        "REACT_APP_API_URL",
      )}/${code}/achievement/${achievementID}`,
      {
        body: JSON.stringify({ achievement: payload }),
        headers: { "Content-Type": "application/json" },
        method: "PATCH",
      },
    );
    if (response.status === 204) {
      return true;
    } else {
      throw await response.json();
    }
  };

  const useAchievementData = (
    achievementID?: string | string[],
  ): SWRResponse<AchievementBEData, any> => {
    const isSingleRequest = typeof achievementID === "string";

    return useSWR(
      () =>
        achievementID && code
          ? isSingleRequest
            ? `${getEnvVariable(
                "REACT_APP_API_URL",
              )}/${code}/achievement/${achievementID}`
            : achievementID.map(
                (id) =>
                  `${getEnvVariable(
                    "REACT_APP_API_URL",
                  )}/${code}/achievement/${id}`,
              )
          : null,
      isSingleRequest ? fetcher : arrayFetcher,
    );
  };

  const useMembersTriggeredAchievements = (
    memberID: string,
    props?: ListingProps,
  ): SWRResponse<ListResponse<TriggeredAchievementListItem>, any> => {
    return useSWR(
      () =>
        code && memberID && props
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${code}/achievement/trigger/member/${memberID}${querystring(props)}`
          : null,
      fetcher,
    );
  };

  return {
    addAchievement,
    addAchievementBatch,
    editAchievement,
    getAchievementBatchData,
    manageProgress,
    patchAchievement,
    useAchievementData,
    useAchievementList,
    useInfiniteAchievmentsList,
    useMembersTriggeredAchievements,
  };
};
